import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';

import * as fromSpaces from '../../../stores/spaces';
import { HMIS, SpacesDispensesHD } from '../../interfaces/IHDspace';
import { LockerActionDataService } from '../../services/locker-action-data/locker-action-data.service';
import { VIEW_TYPES } from '../../utils/constants';

@Component({
  selector: 'frodo-flex-cube-layout',
  templateUrl: './flex-cube-layout.component.html',
  styleUrls: ['./flex-cube-layout.component.scss'],
})
export class FlexCubeLayoutComponent implements OnInit, OnDestroy {
  subscription: Subscription = new Subscription();
  spaces = [];
  maxY = 0;
  state: RouterStateSnapshot;
  accumulatingSpace = [];

  @Input() spacesSelected: string[] = [];
  @Input() positions = [];
  @Input() viewType: string;
  @Output() selectedSpace = new EventEmitter<SpacesDispensesHD>();
  spaces$ = this.store.select(fromSpaces.selectSpaces);

  constructor(private lockerActionDataService: LockerActionDataService, private store: Store) {}

  ngOnInit(): void {
    this.store.dispatch(fromSpaces.loadSpaces());
    this.updateLockerLayout();
  }

  updateLockerLayout(): void {
    this.subscription.add(
      this.spaces$.subscribe((spaces: SpacesDispensesHD[]) => {
        this.spaces = [...spaces];
        spaces.forEach((space, index) => {
          if (index === spaces.length - 1) {
            this.buildHmis(space.hardwareInfo.device.hardwareInfo.hmis);
          }
        });
        this.calculateMaxY();
      })
    );
  }

  buildHmis(hmis: HMIS): void {
    const frontHmis = hmis.front;
    if (frontHmis) {
      frontHmis.forEach((hmi) => {
        if (hmi.coordinates) {
          const HMI = {
            hardwareInfo: {
              coordinates: hmi.coordinates,
            },
          };
          this.spaces = [...this.spaces, HMI];
        }
      });
    }
  }

  calculateMaxY(): void {
    this.maxY = this.spaces.reduce((max, space) => {
      return Math.max(max, space.hardwareInfo.coordinates.y);
    }, 0);
  }

  getStyle(space: SpacesDispensesHD): SpaceStyle {
    const size = 170;
    const padding = 10;
    const totalSize = size + padding * 2;
    return {
      left: `${space.hardwareInfo.coordinates.x * totalSize}px`,
      top: `${(this.maxY - space.hardwareInfo.coordinates.y) * totalSize}px`,
    };
  }

  getSpaceClass(space: SpacesDispensesHD, side: string): string {
    let spaceClass = this.assignDispenseStateClass(space);
    this.spaces.forEach((space) => {
      const x = space?.hardwareInfo?.coordinates?.x;

      if (x === undefined) return;

      if (x > 5 && x < 8) {
        spaceClass += ` four-lockers-${side}`;
      } else if (x > 3 && x < 6) {
        spaceClass += ` three-lockers-${side}`;
      } else if (x > 1 && x < 4) {
        spaceClass += ` two-lockers-${side}`;
      } else if (x >= 0 && x <= 1) {
        spaceClass += ` one-locker-${side}`;
      }
    });

    if (this.spacesSelected.includes(space.id)) {
      spaceClass += ' toggleSelection';
    }

    return spaceClass;
  }

  assignDispenseStateClass(space: SpacesDispensesHD): string {
    if (this.viewType !== VIEW_TYPES.TTW) {
      if (space?.dispenses?.length) {
        return space.dispenses[0].state;
      }
      return space.state ? space.state.selectable : 'space-box';
    }
  }

  getTimerClass(space: SpacesDispensesHD): string {
    return this.lockerActionDataService.getTimerClass(space);
  }

  getOpenedDoorClass(space: SpacesDispensesHD): string {
    return this.lockerActionDataService.getOpenedDoorClass(space, this.positions);
  }

  isSpaceFaulty(space: SpacesDispensesHD): boolean {
    return this.lockerActionDataService.isSpaceFaulty(space);
  }

  handleSelectedSpace(space: SpacesDispensesHD): void {
    this.selectedSpace.emit(space);
  }

  getDispenseTime(space) {
    return this.lockerActionDataService.getDispenseTime(space);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
interface SpaceStyle {
  left: string;
  top: string;
}
